import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, Button, Chip, CircularProgress } from "@mui/material";
import {
    FitnessCenter as DumbbellIcon,
    CheckCircle as CheckIcon,
    DirectionsRun as CardioIcon,
    Timer as TimerIcon,
} from "@mui/icons-material";
import { colors, typography, borders, shadows } from "../../ui/colors";
import { t as ct } from "../../dashboard/coach/coachTokens";
import { imageUrl } from "../../../utils/imageUrl";

/**
 * Render an exercise's secondary line based on its type.
 * Strength: "4×8 · 120s rest · T1_0_1_0"
 * Cardio:   "20 דקות"
 * Catalyst: "3 × 30-60s · 120s rest"
 */
const renderExerciseMeta = (ex) => {
    const type = ex.exerciseType;
    if (type === "CARDIO") {
        const min = Math.round((ex.durationSec || 0) / 60);
        return min > 0 ? `${min} דקות` : `${ex.durationSec || 0} שניות`;
    }
    if (type === "CATALYST") {
        return `${ex.sets || 3} × ${ex.minDuration || 30}-${ex.maxDuration || 60}s · מנוחה ${ex.restSec || 120}s`;
    }
    // strength
    const tempo = ex.tempo && ex.tempo !== "T1_0_1_0" ? ` · ${ex.tempo}` : "";
    return `${ex.sets || 3}×${ex.reps || 10} · מנוחה ${ex.restSec || 120}s${tempo}`;
};

const TypeIcon = ({ type }) => {
    if (type === "CARDIO") return <CardioIcon sx={{ fontSize: 16, color: colors.info }} />;
    if (type === "CATALYST") return <TimerIcon sx={{ fontSize: 16, color: colors.warning }} />;
    return <CheckIcon sx={{ fontSize: 16, color: colors.success }} />;
};

const WorkoutAiPreview = ({ preview, onApply, applying, hasExistingWorkouts }) => {
    const workouts = preview?.workouts || [];
    const totalExercises = workouts.reduce((n, w) => n + (w.exercises || []).length, 0);
    const hasContent = workouts.length > 0;

    const scrollRef = useRef(null);
    const workoutRefs = useRef([]);
    workoutRefs.current = workouts.map((_, i) => workoutRefs.current[i] ?? null);

    const [activeWorkout, setActiveWorkout] = useState(0);

    useEffect(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl || workouts.length === 0) return;

        const handleScroll = () => {
            const viewportTop = scrollEl.getBoundingClientRect().top;
            let best = 0;
            let bestDist = Infinity;
            workoutRefs.current.forEach((el, i) => {
                if (!el) return;
                const dist = Math.abs(el.getBoundingClientRect().top - viewportTop - 20);
                if (dist < bestDist) {
                    bestDist = dist;
                    best = i;
                }
            });
            setActiveWorkout(best);
        };
        scrollEl.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();
        return () => scrollEl.removeEventListener("scroll", handleScroll);
    }, [workouts.length]);

    const scrollToWorkout = useCallback((idx) => {
        const el = workoutRefs.current[idx];
        if (el && scrollRef.current) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, []);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: colors.bg }}>
            {/* Header */}
            <Box
                sx={{
                    padding: "12px 16px",
                    bgcolor: colors.white,
                    borderBottom: borders.default,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    flexShrink: 0,
                }}
            >
                <Box>
                    <Typography sx={{ ...typography.bodySemibold, color: colors.textPrimary }}>
                        תצוגה מקדימה
                    </Typography>
                    <Typography sx={{ ...typography.tiny, color: colors.textMuted }}>
                        {workouts.length} ימי אימון · {totalExercises} תרגילים
                    </Typography>
                </Box>
            </Box>

            {/* Workout navigator */}
            {hasContent && workouts.length > 1 && (
                <Box
                    sx={{
                        display: "flex",
                        gap: 0.5,
                        padding: "8px 12px",
                        bgcolor: colors.white,
                        borderBottom: borders.default,
                        overflowX: "auto",
                        flexShrink: 0,
                        "&::-webkit-scrollbar": { height: 4 },
                        "&::-webkit-scrollbar-thumb": { bgcolor: colors.border, borderRadius: 2 },
                    }}
                >
                    {workouts.map((w, idx) => {
                        const isActive = idx === activeWorkout;
                        return (
                            <Chip
                                key={idx}
                                label={`${idx + 1}. ${w.nameHeb || w.name || `אימון ${idx + 1}`}`}
                                onClick={() => scrollToWorkout(idx)}
                                size="small"
                                sx={{
                                    fontFamily: typography.fontFamily,
                                    ...typography.tiny,
                                    bgcolor: isActive ? ct.green : colors.white,
                                    color: isActive ? colors.white : colors.textSecondary,
                                    border: `1px solid ${isActive ? ct.green : colors.border}`,
                                    cursor: "pointer",
                                    flexShrink: 0,
                                    "&:hover": {
                                        bgcolor: isActive ? ct.greenDark : ct.greenLight,
                                    },
                                }}
                            />
                        );
                    })}
                </Box>
            )}

            {/* Workout list */}
            <Box
                ref={scrollRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "auto",
                    padding: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                }}
            >
                {!hasContent && (
                    <Box sx={{ textAlign: "center", mt: 6, color: colors.textMuted }}>
                        <DumbbellIcon sx={{ fontSize: 56, opacity: 0.3, mb: 1 }} />
                        <Typography sx={{ ...typography.body, color: colors.textSecondary }}>
                            התוכנית תיבנה כאן בזמן אמת
                        </Typography>
                        <Typography sx={{ ...typography.small, color: colors.textMuted, mt: 0.5 }}>
                            ענה על השאלות מימין והעוזר יתחיל להרכיב את התוכנית
                        </Typography>
                    </Box>
                )}

                {workouts.map((workout, workoutIdx) => (
                    <Box
                        key={workoutIdx}
                        ref={(el) => { workoutRefs.current[workoutIdx] = el; }}
                        sx={{
                            bgcolor: colors.white,
                            border: borders.default,
                            borderRadius: borders.radius.md,
                            boxShadow: shadows.xs,
                            scrollMarginTop: "8px",
                        }}
                    >
                        <Box sx={{
                            padding: "10px 12px",
                            bgcolor: ct.greenLight,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            borderTopLeftRadius: borders.radius.md,
                            borderTopRightRadius: borders.radius.md,
                            borderBottom: borders.default,
                        }}>
                            <Typography sx={{ ...typography.bodySemibold, color: colors.textPrimary }}>
                                {workoutIdx + 1}. {workout.nameHeb || workout.name || `אימון ${workoutIdx + 1}`}
                            </Typography>
                            <Typography sx={{ ...typography.tiny, color: colors.textSecondary }}>
                                {(workout.exercises || []).length} תרגילים
                            </Typography>
                        </Box>

                        {(workout.exercises || []).length === 0 ? (
                            <Box sx={{ padding: "8px 12px" }}>
                                <Typography sx={{ ...typography.small, color: colors.textMuted }}>
                                    ממתין לתרגילים...
                                </Typography>
                            </Box>
                        ) : (
                            (workout.exercises || []).map((ex, exerciseIdx) => (
                                <Box
                                    key={exerciseIdx}
                                    sx={{
                                        padding: "8px 12px",
                                        borderTop: exerciseIdx > 0 ? borders.default : 0,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <TypeIcon type={ex.exerciseType} />
                                    {ex.imageUrl && (
                                        <Box
                                            component="img"
                                            src={imageUrl(ex.imageUrl, 36)}
                                            alt=""
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                objectFit: "cover",
                                                borderRadius: borders.radius.sm,
                                                border: borders.default,
                                                flexShrink: 0,
                                            }}
                                        />
                                    )}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                ...typography.smallSemibold,
                                                color: colors.textPrimary,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {ex.exerciseName || ex.nameHeb || ex.name || "—"}
                                        </Typography>
                                        <Typography sx={{ ...typography.tiny, color: colors.textMuted }}>
                                            {renderExerciseMeta(ex)}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))
                        )}
                    </Box>
                ))}
            </Box>

            {/* Footer */}
            {hasContent && (
                <Box sx={{
                    padding: "12px 16px",
                    borderTop: borders.default,
                    bgcolor: colors.white,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    flexShrink: 0,
                }}>
                    <Button
                        variant="contained"
                        onClick={onApply}
                        disabled={applying}
                        startIcon={applying ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : null}
                        sx={{
                            bgcolor: ct.green,
                            color: colors.white,
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: borders.radius.md,
                            "&:hover": { bgcolor: ct.greenDark },
                        }}
                    >
                        {applying ? "מחיל..." : hasExistingWorkouts ? "החל על התבנית..." : "החל על התבנית"}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default WorkoutAiPreview;
